import { forEach } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';

import { confetti } from '@shared/utils/helpers';

import { buildGridForRegistration } from '../../../../libs/registrationUtils';
import { ajaxErrorHandler, defaultSuccess } from '../../../../libs/utils';
import {
  approveCrewQuantity,
  generateVouchers,
  rejectCrewQuantity,
  sendCrewQuantityRequest,
  sendVouchers,
  updateRegistrationProfile,
} from '../../../../services/api/registrations';
import HotTable from '../../../vendor/handsontable/react-handsontable.jsx';

class RegistrationProfileLimits extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      columns: [],
      rows: [],
      isSubmitting: false,
    };

    this._rowGetter = this._rowGetter.bind(this);
    this._handleGridRowsUpdated = this._handleGridRowsUpdated.bind(this);
    this._sendPlanningInvite = this._sendPlanningInvite.bind(this);
    this._approve = this._approve.bind(this);
    this._reject = this._reject.bind(this);
    this._handleProfileLimitsUpdate = this._handleProfileLimitsUpdate.bind(this);
    this._generateAndSendVouchers = this._generateAndSendVouchers.bind(this);
    this._sendAllVouchers = this._sendAllVouchers.bind(this);

    this.reasonRef = React.createRef();
    this.tableRef = React.createRef();
  }

  UNSAFE_componentWillMount() {
    buildGridForRegistration(this.props.registration, true).then((grid) => {
      this.setState({
        ...grid,
      });
    });
  }

  _handleGridRowsUpdated({ fromRow, toRow, updated }) {
    let rows = this.state.rows.slice();

    for (let i = fromRow; i <= toRow; i++) {
      let rowToUpdate = rows[i];
      let updatedRow = { ...rowToUpdate, ...updated };
      rows[i] = updatedRow;
    }

    this.setState({ rows });
  }

  _rowGetter(i) {
    return this.state.rows[i];
  }

  _sendPlanningInvite() {
    sendCrewQuantityRequest(this.props.registration.id, {})
      .then(() => {
        defaultSuccess('Registration has been send!');
        this.props.refreshRegistration();
      })
      .catch(ajaxErrorHandler);
  }

  _approve() {
    approveCrewQuantity(this.props.registration.id, {
      reason: this.reasonRef.current ? this.reasonRef.current.value : '',
    })
      .then(() => {
        defaultSuccess('Registration has been approved!');
        confetti();
        this.props.refreshRegistration();
      })
      .catch(ajaxErrorHandler);
  }

  _reject() {
    rejectCrewQuantity(this.props.registration.id, {
      reason: this.reasonRef.current.value,
    })
      .then(() => {
        defaultSuccess('Registration has been rejected!');
        this.props.refreshRegistration();
      })
      .catch(ajaxErrorHandler);
  }

  _handleProfileLimitsUpdate() {
    const { data } = this.state,
      { registration } = this.props,
      tableData = this.tableRef.current.hotInstance.getData();

    let promisses = [];

    this.setState({
      isSubmitting: true,
    });

    registration.registrationProfiles.forEach((regProfile, profileIndex) => {
      const profile = {
        amountPerDay: [],
        profileId: regProfile.profile.id,
      };

      // Due to previous year's data, each profile takes up two columns (current data + previous data)
      const tableHasPreviousColumns = registration.registrationProfiles.length * 2 === tableData[0].length;

      const colIndex = tableHasPreviousColumns ? profileIndex * 2 : profileIndex;

      data.forEach((row, rowIndex) => {
        profile.amountPerDay.push({
          day: row.day,
          amount: tableData[rowIndex][colIndex],
        });
      });

      promisses.push(updateRegistrationProfile(registration.id, regProfile.id, profile));
    });

    Promise.all(promisses)
      .then(() => {
        defaultSuccess('Profile limits have been updated!');
      })
      .catch(ajaxErrorHandler)
      .then(() => {
        this.setState({
          isSubmitting: false,
        });
      });
  }

  static propTypes = {
    registration: PropTypes.object,
    refreshRegistration: PropTypes.func,
  };

  _generateAndSendVouchers() {
    generateVouchers(this.props.registration.id, {
      priority: true,
      sendVouchers: true,
    })
      .then((response) => {
        defaultSuccess('Vouchers are being generated and sent. You will receive them in a couple of minutes.');
      })
      .catch(ajaxErrorHandler);
  }

  _sendAllVouchers() {
    sendVouchers(this.props.registration.id)
      .then((response) => {
        defaultSuccess('Vouchers are being sent. You will receive them in a couple of minutes.');
      })
      .catch(ajaxErrorHandler);
  }

  render() {
    const { columns, data, colHeaders, rowHeaders, isSubmitting } = this.state;
    const { registration } = this.props;

    return (
      <div className="row">
        <div className="col-md-8">
          <div className="m-portlet">
            <div className="m-portlet__head">
              <div className="m-portlet__head-caption">
                <div className="m-portlet__head-title">
                  <span className="m-portlet__head-icon">
                    <i className="flaticon-line-graph" />
                  </span>
                  <h3 className="m-portlet__head-text">Planning</h3>
                </div>
              </div>
            </div>
            <div className="m-portlet__body">
              <div className="form-group m-form__group">
                {columns.length > 0 && (
                  <HotTable
                    root="catering"
                    data={data}
                    colHeaders={colHeaders}
                    rowHeaders={rowHeaders}
                    columns={columns}
                    preventOverflow="horizontal"
                    stretchH="all"
                    rowHeaderWidth={100}
                    height={35 + data.length * 32 + 5}
                    ref={this.tableRef}
                    fillHandle={{
                      autoInsertRow: false,
                    }}
                  />
                )}
              </div>
            </div>
            <div className="m-portlet__foot m--align-right">
              <button
                type="submit"
                className={
                  'btn btn-brand' + (isSubmitting ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
                }
                onClick={this._handleProfileLimitsUpdate}
              >
                Update
              </button>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="m-portlet">
            <div className="m-portlet__head">
              <div className="m-portlet__head-caption">
                <div className="m-portlet__head-title">
                  <span className="m-portlet__head-icon">
                    <i className="flaticon-rocket" />
                  </span>
                  <h3 className="m-portlet__head-text">Actions</h3>
                </div>
              </div>
            </div>
            <div className="m-portlet__body">
              {registration.status.name === 'created' && (
                <div>
                  {(registration.registrationProfiles.length === 0 || registration.parkings.length === 0) && (
                    <div className="m-alert m-alert--icon m-alert--outline alert alert-primary" role="alert">
                      <div className="m-alert__icon">
                        <i className="la la-warning" />
                      </div>
                      <div className="m-alert__text">
                        <Trans>RegistrationNeedsProfilesAndParkingMsg</Trans>
                      </div>
                      <div className="m-alert__actions">
                        <Link
                          to={`/admin/registrations/${registration.id}/edit`}
                          className="btn btn-primary btn-sm m-btn m-btn--pill m-btn--wide"
                        >
                          <Trans>Fix</Trans>
                        </Link>
                      </div>
                    </div>
                  )}
                  {registration.registrationProfiles.length > 0 && registration.parkings.length > 0 && (
                    <div>
                      <button
                        className="btn btn-accent m-btn m-btn--custom m-btn--icon btn-block"
                        onClick={this._sendPlanningInvite}
                      >
                        <span>
                          <i className="la la-send" />
                          <span>
                            <Trans>sendPlanningInviteButton</Trans>
                          </span>
                        </span>
                      </button>
                      <button
                        className="btn btn-success m-btn m-btn--custom m-btn--icon btn-block"
                        onClick={this._approve}
                      >
                        <span>
                          <i className="la la-calendar-check-o" />
                          <span>
                            <Trans>Approve</Trans>
                          </span>
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {registration.status.name === 'crewQuantityRequested' && (
                <div>
                  <p>
                    <Trans>PlanningRequestSendMsg</Trans>
                  </p>
                  <button
                    className="btn btn-accent m-btn m-btn--custom m-btn--icon btn-block"
                    onClick={this._sendPlanningInvite}
                  >
                    <span>
                      <i className="la la-send" />
                      <span>
                        <Trans>ResendPlanningInviteButton</Trans>
                      </span>
                    </span>
                  </button>
                  <button className="btn btn-success m-btn m-btn--custom m-btn--icon btn-block" onClick={this._approve}>
                    <span>
                      <i className="la la-calendar-check-o" />
                      <span>
                        <Trans>Approve</Trans>
                      </span>
                    </span>
                  </button>
                </div>
              )}

              {registration.status.name === 'crewQuantityWaitingForApproval' && (
                <div className="m-form">
                  <div className="alert m-alert m-alert--default" role="alert">
                    {registration.status.reason}
                  </div>

                  <div className="form-group m-form__group">
                    <label htmlFor="planningRemark">
                      <Trans>Extra Remarks</Trans>
                    </label>
                    <textarea className="form-control m-input" id="planningRemark" rows="5" ref={this.reasonRef} />
                  </div>
                  <button className="btn btn-success m-btn m-btn--custom m-btn--icon btn-block" onClick={this._approve}>
                    <span>
                      <i className="la la-calendar-check-o" />
                      <span>
                        <Trans>Approve</Trans>
                      </span>
                    </span>
                  </button>
                  <button className="btn btn-danger m-btn m-btn--custom m-btn--icon btn-block" onClick={this._reject}>
                    <span>
                      <i className="la la-warning" />
                      <span>
                        <Trans>Reject</Trans>
                      </span>
                    </span>
                  </button>
                </div>
              )}

              {registration.status.name === 'approved' && (
                <div>
                  <button
                    className="btn btn-success m-btn m-btn--custom m-btn--icon btn-block"
                    onClick={this._generateAndSendVouchers}
                  >
                    <span>
                      <i className="la la-send" />
                      <span>
                        <Trans>Generate & Send NEW Vouchers</Trans>
                      </span>
                    </span>
                  </button>
                  <button
                    className="btn btn-outline-accent m-btn m-btn--custom m-btn--icon btn-block"
                    onClick={this._sendAllVouchers}
                  >
                    <span>
                      <i className="la la-send" />
                      <span>
                        <Trans>Send ALL Vouchers</Trans>
                      </span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default RegistrationProfileLimits;
